// src/pages/Admin.js
// ──────────────────────────────────────────────────────────────────────────
// Admin dashboard (staff only). Three tabs:
//   • Applications — view submissions, save any one as PDF, export all as CSV
//   • Content      — edit contact details, hero slides (with image upload),
//                    and mission/vision/purpose
//   • News         — add / edit / delete news posts (with image upload)
//
// Access: must be logged in AND listed in the Supabase "admins" table.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import {
  fetchApplications, setApplicationStatus, suggestStudentId, onboardApplicant, sendApplicantWelcome,
  fetchSiteContent, saveSiteContent,
  uploadMedia, fetchNews, saveNews, removeNews,
  fetchPrograms, saveProgram, removeProgram,
  fetchNotices, saveNotice, removeNotice,
  fetchDocuments, saveDocument, removeDocument,
  fetchResults, saveResult, removeResult,
  createStudentAccount, createStaffAccount, fetchStudents, fetchStaff,
  setProfileRole, resolveAccountEmail,
  fetchLibrary, saveLibraryItem, removeLibraryItem, uploadLibraryFile, signLibraryFile,
  fetchAdmins, addAdmin, removeAdmin, setAdminRole,
} from '../lib/cms';
import { FACULTIES, LEVELS } from '../api/programs';
import { BUILT_IN_LIBRARY, LIBRARY_DEPTS, LIBRARY_TYPES, librarySlug } from '../api/library';
import theme from '../theme/theme';

// ── Layout ──────────────────────────────────────────────────
const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff; padding: 5.5rem ${theme.layout.gutter} 2.5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2rem, 5vw, 3rem); color: #fff; margin-top: 0.8rem; }
  .who { margin-top: 0.6rem; font-size: 0.85rem; color: rgba(255,255,255,0.7); }
  .who button { background: none; border: none; color: ${theme.color.lemon}; font-weight: 700; cursor: pointer; font-family: inherit; }
  .kicker { color: #fff; }
`;
const Tabs = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 0 ${theme.layout.gutter};
  display: flex; gap: 0.5rem; border-bottom: 1px solid ${theme.color.line};
  overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${theme.color.line}; border-radius: 4px; }
  button { background: none; border: none; cursor: pointer; font-family: ${theme.font.body}; font-size: 0.95rem; font-weight: 600;
           color: ${theme.color.inkSoft}; padding: 1rem 1.1rem; border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; flex: 0 0 auto; }
  button.active { color: ${theme.color.accent}; border-bottom-color: ${theme.color.accent}; }
`;
const Section = styled.section` max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 2.5rem ${theme.layout.gutter} 5rem; `;
const Note = styled.div`
  max-width: 520px; margin: 5rem auto; text-align: center; padding: 0 1.5rem;
  h2 { font-weight: 400; font-size: 1.8rem; } p { color: ${theme.color.inkSoft}; margin: 0.6rem 0 1.4rem; }
  a { background: ${theme.color.accent}; color: #fff; font-weight: 700; padding: 12px 26px; border-radius: ${theme.layout.radius}; }
`;

// shared form bits
const Field = styled.div`
  margin-bottom: 1rem;
  label { display: block; font-size: 0.76rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${theme.color.inkSoft}; margin-bottom: 0.35rem; }
  input, textarea {
    width: 100%; font-family: ${theme.font.body}; font-size: 0.96rem; padding: 10px 13px;
    border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; color: ${theme.color.ink};
  }
  input:focus, textarea:focus { outline: none; border-color: ${theme.color.accent}; }
  textarea { min-height: 90px; resize: vertical; }
`;
const Btn = styled.button`
  background: ${(p) => (p.$ghost ? '#fff' : theme.color.accent)}; color: ${(p) => (p.$ghost ? theme.color.ink : '#fff')};
  border: 1px solid ${(p) => (p.$ghost ? theme.color.line : theme.color.accent)};
  font-family: ${theme.font.body}; font-weight: 700; font-size: 0.86rem; padding: 10px 18px;
  border-radius: ${theme.layout.radius}; cursor: pointer; &:disabled { opacity: 0.6; cursor: default; }
`;
const Saved = styled.span` margin-left: 0.8rem; font-size: 0.85rem; color: #1a7f4b; `;
const Card = styled.div` border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.6rem; background: #fff; margin-bottom: 1.4rem; `;

// ── Applications tab ────────────────────────────────────────
const Table = styled.div`
  border-top: 1px solid ${theme.color.line};
  .row { display: grid; grid-template-columns: 1.4fr 1fr 1.4fr auto; gap: 1rem; align-items: center;
         padding: 0.85rem 0; border-bottom: 1px solid ${theme.color.line}; font-size: 0.92rem; }
  .row .muted { color: ${theme.color.inkSoft}; }
  .row .acts { display: flex; gap: 0.5rem; }
  @media (max-width: 680px) { .row { grid-template-columns: 1fr; gap: 0.3rem; } .row .acts { margin-top: 0.4rem; } }
  .row.clickable { cursor: pointer; }
  .row.clickable:hover { background: #F7F9FC; }
`;

// Full-screen detail panel for one application.
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(19,33,61,0.45); z-index: 60;
  display: flex; justify-content: flex-end;
`;
const Panel = styled.div`
  background: #fff; width: min(560px, 100%); height: 100%; overflow-y: auto;
  padding: 2rem 2rem 4rem;
  h2 { font-weight: 400; font-size: 1.6rem; margin: 0.2rem 0 0.2rem; }
  .meta { color: ${theme.color.inkSoft}; font-size: 0.85rem; margin-bottom: 1.6rem; }
  .close { position: sticky; top: 0; float: right; background: none; border: none; font-size: 1.6rem;
           line-height: 1; cursor: pointer; color: ${theme.color.inkSoft}; }
  dl { display: grid; grid-template-columns: 130px 1fr; gap: 0.55rem 1rem; font-size: 0.94rem; margin: 0; }
  dt { color: ${theme.color.inkSoft}; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.05em;
       text-transform: uppercase; padding-top: 0.15rem; }
  dd { margin: 0; white-space: pre-wrap; word-break: break-word; }
  dd a { color: ${theme.color.accent}; font-weight: 600; }
  .acts { display: flex; gap: 0.6rem; margin-top: 2rem; flex-wrap: wrap; }
  @media (max-width: 600px) { dl { grid-template-columns: 1fr; gap: 0.15rem; } dt { margin-top: 0.7rem; } }
`;

const FIELDS = [
  ['Full name', 'name'], ['Date of birth', 'dob'], ['Gender', 'gender'],
  ['Phone', 'phone'], ['Email', 'email'], ['Town / area', 'town'],
  ['Department', 'department'], ['Course', 'course'],
  ['Education', 'education'], ['Funding', 'funding'], ['Notes', 'notes'],
];

// ── Review status ───────────────────────────────────────────
const STATUS = {
  pending:  { label: 'Pending',  fg: '#8a6100', bg: '#FDF3D6' },
  approved: { label: 'Approved', fg: '#1a7f4b', bg: '#E3F5EB' },
  rejected: { label: 'Rejected', fg: '#b3261e', bg: '#FCE8E6' },
  enrolled: { label: 'Enrolled', fg: '#1c4ed8', bg: '#E5EDFD' },
};
const Badge = styled.span`
  display: inline-block; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; padding: 3px 9px; border-radius: 999px;
  color: ${(p) => STATUS[p.$s]?.fg || theme.color.inkSoft};
  background: ${(p) => STATUS[p.$s]?.bg || '#eee'};
`;
const Filters = styled.div`
  display: flex; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 1rem;
  button { background: #fff; border: 1px solid ${theme.color.line}; border-radius: 999px;
           font-family: ${theme.font.body}; font-size: 0.82rem; font-weight: 600; color: ${theme.color.inkSoft};
           padding: 6px 14px; cursor: pointer; }
  button.on { background: ${theme.color.accent}; border-color: ${theme.color.accent}; color: #fff; }
`;

// Onboarding form shown once an application is approved.
function OnboardBox({ a, onDone }) {
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState(null);   // credentials to show once created
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [mailMsg, setMailMsg] = useState('');

  useEffect(() => { suggestStudentId().then(setStudentId).catch(() => {}); }, []);

  const sendMail = async () => {
    setMailMsg(''); setSending(true);
    try {
      const res = await sendApplicantWelcome(a.id, studentId);
      setSent(true);
      setMailMsg(`Sent to ${res.to || a.email}.`);
    } catch (e) { setMailMsg('Error: ' + (e.message || 'could not send the email')); }
    finally { setSending(false); }
  };

  // Readable temporary password — no ambiguous characters to misread aloud.
  const generate = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let out = '';
    for (let i = 0; i < 10; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
    setPassword(out);
  };

  const go = async () => {
    setMsg(''); setBusy(true);
    try {
      const res = await onboardApplicant(a, { studentId, password });
      // Stay on screen: this is the only time the password is ever shown.
      setDone({ studentId, password, warning: res.linkWarning });
    } catch (e) { setMsg('Error: ' + (e.message || 'could not create the account')); }
    finally { setBusy(false); }
  };

  if (done) {
    const creds = `Student ID: ${done.studentId}\nTemporary password: ${done.password}`;
    return (
      <div style={{ marginTop: '1.6rem', padding: '1.2rem', border: '1px solid #1a7f4b', borderRadius: theme.layout.radius, background: '#E3F5EB' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#1a7f4b' }}>✓ Account created</h3>
        <p style={{ fontSize: '0.88rem', marginBottom: '0.8rem' }}>
          Write these down before closing — the password can't be shown again. {a.name} will be asked to
          choose their own password the first time they sign in.
        </p>
        <pre style={{ background: '#fff', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius,
                      padding: '0.9rem', fontSize: '0.9rem', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{creds}</pre>
        {done.warning && (
          <p style={{ fontSize: '0.85rem', color: '#8a6100', marginTop: '0.7rem' }}>
            Note: the account exists, but linking it to this application failed ({done.warning}). You may need to set the status by hand.
          </p>
        )}

        {/* Congratulations email — carries the ID and a set-password link, never the password. */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #bfe3cf' }}>
          {a.email ? (
            <>
              <p style={{ fontSize: '0.88rem', marginBottom: '0.6rem' }}>
                Email <strong>{a.email}</strong> a congratulations message with their Student ID and a
                one-time link to set their own password.
              </p>
              <Btn onClick={sendMail} disabled={sending || sent}>
                {sending ? 'Sending…' : sent ? 'Sent ✓' : 'Send welcome email'}
              </Btn>
              {mailMsg && (
                <p style={{ marginTop: '0.7rem', fontSize: '0.85rem', color: mailMsg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{mailMsg}</p>
              )}
            </>
          ) : (
            <p style={{ fontSize: '0.85rem', color: '#8a6100', margin: 0 }}>
              No email address on this application — give {a.name} their details in person or by phone
              {a.phone ? ` (${a.phone})` : ''}.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <Btn $ghost onClick={() => { navigator.clipboard?.writeText(creds); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? 'Copied ✓' : 'Copy details'}
          </Btn>
          <Btn onClick={onDone}>Done</Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1.6rem', padding: '1.2rem', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, background: '#F7F9FC' }}>
      <h3 style={{ fontSize: '1rem', marginBottom: '0.8rem' }}>Onboard as a student</h3>
      <Field><label>Student ID</label><input value={studentId} onChange={(e) => setStudentId(e.target.value)} placeholder="MTI/2026/001" /></Field>
      <Field>
        <label>Temporary password</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="give this to the student" />
          <Btn $ghost onClick={generate} type="button">Generate</Btn>
        </div>
      </Field>
      <Btn onClick={go} disabled={busy || !studentId || !password}>{busy ? 'Creating…' : 'Create student account'}</Btn>
      {msg && <p style={{ marginTop: '0.9rem', fontSize: '0.88rem', color: '#b3261e' }}>{msg}</p>}
    </div>
  );
}

function ApplicationDetail({ a, onClose, onChanged }) {
  const { isOwner } = useAuth();
  const [busy, setBusy] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const status = a.status || 'pending';

  // Re-issue the welcome email — the set-password link expires, so an admin
  // needs to be able to send a fresh one without recreating the account.
  const resend = async () => {
    setResendMsg(''); setResending(true);
    try {
      await sendApplicantWelcome(a.id, a.student_id);
      setResendMsg(`Sent to ${a.email}.`);
      onChanged({ ...a, notified_at: new Date().toISOString() });
    } catch (e) { setResendMsg('Error: ' + (e.message || 'could not send the email')); }
    finally { setResending(false); }
  };

  const decide = async (next) => {
    if (next === 'rejected' && !window.confirm(`Reject ${a.name}'s application?`)) return;
    setBusy(next);
    try { const row = await setApplicationStatus(a.id, next); onChanged(row || { ...a, status: next }); }
    catch (e) { alert(e.message || 'Could not update the application.'); }
    finally { setBusy(''); }
  };

  // Close on Escape, and stop the page behind from scrolling.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const value = (key) => {
    const v = a[key];
    if (!v) return <span style={{ color: theme.color.inkSoft }}>—</span>;
    if (key === 'email') return <a href={`mailto:${v}`}>{v}</a>;
    if (key === 'phone') return <a href={`tel:${String(v).replace(/\s/g, '')}`}>{v}</a>;
    return v;
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Application details">
        <button className="close" onClick={onClose} aria-label="Close">×</button>
        <span style={{ fontSize: '0.76rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.color.accent }}>
          Application
        </span>
        <h2>{a.name || 'Unnamed applicant'}</h2>
        <p className="meta">
          {a.course || 'No course given'}
          {a.created_at ? ` · submitted ${new Date(a.created_at).toLocaleString()}` : ''}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '1.6rem' }}>
          <Badge $s={status}>{STATUS[status]?.label || status}</Badge>
          {a.reviewed_by && a.reviewed_at && (
            <span style={{ fontSize: '0.8rem', color: theme.color.inkSoft }}>
              by {a.reviewed_by} · {new Date(a.reviewed_at).toLocaleDateString()}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.8rem' }}>
          {!isOwner && (
            <p style={{ fontSize: '0.85rem', color: theme.color.inkSoft, margin: 0 }}>
              Approving, rejecting and onboarding create real accounts, so they’re reserved for owners.
            </p>
          )}
          {isOwner && status !== 'enrolled' && (
            <>
              {status !== 'approved' && (
                <Btn onClick={() => decide('approved')} disabled={Boolean(busy)}>
                  {busy === 'approved' ? 'Saving…' : 'Approve'}
                </Btn>
              )}
              {status !== 'rejected' && (
                <Btn $ghost onClick={() => decide('rejected')} disabled={Boolean(busy)}>
                  {busy === 'rejected' ? 'Saving…' : 'Reject'}
                </Btn>
              )}
              {status !== 'pending' && (
                <Btn $ghost onClick={() => decide('pending')} disabled={Boolean(busy)}>
                  {busy === 'pending' ? 'Saving…' : 'Back to pending'}
                </Btn>
              )}
            </>
          )}
          {isOwner && status === 'enrolled' && (
            <div>
              <p style={{ fontSize: '0.88rem', color: '#1a7f4b', margin: '0 0 0.6rem' }}>
                ✓ A student account has been created from this application.
                {a.notified_at && ` Welcome email sent ${new Date(a.notified_at).toLocaleDateString()}.`}
              </p>
              {a.email && (
                <>
                  <Btn $ghost onClick={resend} disabled={resending}>
                    {resending ? 'Sending…' : a.notified_at ? 'Resend welcome email' : 'Send welcome email'}
                  </Btn>
                  {resendMsg && (
                    <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: resendMsg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{resendMsg}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* The action to take next — keep it above the field list, not buried
            under eleven rows of applicant detail. */}
        {isOwner && status === 'approved' && (
          <OnboardBox a={a} onDone={() => onChanged({ ...a, status: 'enrolled' })} />
        )}

        <dl>
          {FIELDS.map(([label, key]) => (
            <React.Fragment key={key}>
              <dt>{label}</dt>
              <dd>{value(key)}</dd>
            </React.Fragment>
          ))}
        </dl>

        <div className="acts">
          <Btn $ghost onClick={() => printApplication(a)}>Save as PDF</Btn>
          {a.email && <Btn $ghost as="a" href={`mailto:${a.email}`} style={{ textDecoration: 'none' }}>Email applicant</Btn>}
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </Panel>
    </Overlay>
  );
}

function printApplication(a) {
  const row = (k, v) => `<tr><td style="color:#5A6880;padding:6px 14px 6px 0;white-space:nowrap;vertical-align:top">${k}</td><td style="padding:6px 0">${v || '—'}</td></tr>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>Application — ${a.name || ''}</title>
    <meta charset="utf-8"/>
    <style>body{font-family:Arial,Helvetica,sans-serif;color:#13213D;padding:40px;max-width:720px;margin:auto}
    h1{font-size:22px;margin:0 0 4px} .sub{color:#5A6880;font-size:13px;margin-bottom:24px}
    table{border-collapse:collapse;font-size:14px;width:100%} .foot{margin-top:30px;font-size:11px;color:#9aa3b5}</style></head>
    <body>
      <h1>CGA Mbala Training Institute</h1>
      <div class="sub">Application — submitted ${a.created_at ? new Date(a.created_at).toLocaleString() : ''}</div>
      <table>
        ${row('Full name', a.name)}${row('Date of birth', a.dob)}${row('Gender', a.gender)}
        ${row('Phone', a.phone)}${row('Email', a.email)}${row('Town / area', a.town)}
        ${row('Department', a.department)}${row('Course', a.course)}
        ${row('Education', a.education)}${row('Funding', a.funding)}${row('Notes', a.notes)}
      </table>
      <div class="foot">Generated from the MTI admin dashboard.</div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}

function exportCSV(rows) {
  const cols = ['created_at', 'status', 'name', 'dob', 'gender', 'phone', 'email', 'town', 'department', 'course', 'education', 'funding', 'notes', 'reviewed_by', 'reviewed_at'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url; link.download = `mti-applications-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click(); URL.revokeObjectURL(url);
}

function Applications() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);      // the application being viewed
  const [status, setStatus] = useState('all'); // review filter

  useEffect(() => {
    fetchApplications().then(setRows).catch((e) => setErr(e.message || 'Could not load applications.'));
  }, []);

  // Patch one row in place after a decision, and keep the open panel in sync.
  const applyChange = (row) => {
    setRows((all) => (all || []).map((r) => (r.id === row.id ? { ...r, ...row } : r)));
    setOpen((cur) => (cur && cur.id === row.id ? { ...cur, ...row } : cur));
  };

  const counts = useMemo(() => {
    const c = { all: rows?.length || 0, pending: 0, approved: 0, rejected: 0, enrolled: 0 };
    (rows || []).forEach((r) => { c[r.status || 'pending'] += 1; });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== 'all' && (r.status || 'pending') !== status) return false;
      if (!s) return true;
      return `${r.name} ${r.phone} ${r.email} ${r.course} ${r.department}`.toLowerCase().includes(s);
    });
  }, [rows, q, status]);

  if (err) return <p style={{ color: '#b3261e' }}>{err}</p>;
  if (!rows) return <p style={{ color: theme.color.inkSoft }}>Loading applications…</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginBottom: '1.4rem', flexWrap: 'wrap' }}>
        <input placeholder="Search name, phone, course…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ flex: '1 1 240px', padding: '10px 14px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
        <Btn $ghost onClick={() => exportCSV(rows)} disabled={!rows.length}>Export all (CSV)</Btn>
        <span style={{ color: theme.color.inkSoft, fontSize: '0.85rem' }}>{filtered.length} of {rows.length}</span>
      </div>
      {rows.length > 0 && (
        <>
          <Filters>
            {['all', 'pending', 'approved', 'rejected', 'enrolled'].map((k) => (
              <button key={k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
                {k === 'all' ? 'All' : STATUS[k].label} ({counts[k]})
              </button>
            ))}
          </Filters>
          <p style={{ color: theme.color.inkSoft, fontSize: '0.82rem', marginBottom: '1rem' }}>
            Click any application to read it in full and approve or reject it.
          </p>
        </>
      )}
      {rows.length === 0 ? (
        <p style={{ color: theme.color.inkSoft }}>No applications yet.</p>
      ) : (
        <Table>
          {filtered.map((a) => (
            <div className="row clickable" key={a.id} onClick={() => setOpen(a)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(a); } }}>
              <div>
                <strong>{a.name}</strong>
                <div className="muted">
                  {a.phone}{a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <div><Badge $s={a.status || 'pending'}>{STATUS[a.status || 'pending']?.label || a.status}</Badge></div>
              <div>{a.course}<div className="muted">{a.department} · {a.funding}</div></div>
              <div className="acts"><Btn $ghost onClick={(e) => { e.stopPropagation(); setOpen(a); }}>View</Btn></div>
            </div>
          ))}
        </Table>
      )}

      {rows.length > 0 && filtered.length === 0 && (
        <p style={{ color: theme.color.inkSoft, marginTop: '1.4rem' }}>No applications match this filter.</p>
      )}

      {open && <ApplicationDetail a={open} onClose={() => setOpen(null)} onChanged={applyChange} />}
    </>
  );
}

// ── Content tab ─────────────────────────────────────────────
function ImageField({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const url = await uploadMedia(file); onChange(url); } catch (er) { alert(er.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <Field>
      <label>{label}</label>
      <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
        {value ? <img src={value} alt="" style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 4, border: `1px solid ${theme.color.line}` }} /> : null}
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
        <label style={{ whiteSpace: 'nowrap', cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>
          {busy ? 'Uploading…' : 'Upload'}
          <input type="file" accept="image/*" onChange={pick} style={{ display: 'none' }} />
        </label>
      </div>
    </Field>
  );
}

function Content() {
  const [loaded, setLoaded] = useState(false);
  const [contact, setContact] = useState({ email: '', phone: '', address: '', postal: '' });
  const [intro, setIntro] = useState({ purpose: '', mission: '', vision: '' });
  const [slides, setSlides] = useState([]);
  const [director, setDirector] = useState({ role: 'Director', name: '', photo: '', bio: '' });
  const [leaders, setLeaders] = useState([]);
  const [savedKey, setSavedKey] = useState('');

  useEffect(() => {
    fetchSiteContent().then((c) => {
      if (c.contact) setContact((p) => ({ ...p, ...c.contact }));
      if (c.intro) setIntro((p) => ({ ...p, ...c.intro }));
      if (Array.isArray(c.heroSlides)) setSlides(c.heroSlides);
      if (c.leadership?.director) setDirector((p) => ({ ...p, ...c.leadership.director }));
      if (Array.isArray(c.leadership?.leaders)) setLeaders(c.leadership.leaders);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const flash = (k) => { setSavedKey(k); setTimeout(() => setSavedKey(''), 2500); };
  const save = async (key, value) => { try { await saveSiteContent(key, value); flash(key); } catch (e) { alert(e.message || 'Save failed'); } };

  const setSlide = (i, patch) => setSlides((arr) => arr.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSlide = () => setSlides((arr) => [...arr, { kicker: '', title: '', sub: '', image: '' }]);
  const delSlide = (i) => setSlides((arr) => arr.filter((_, j) => j !== i));

  const setLeader = (i, patch) => setLeaders((arr) => arr.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addLeader = () => setLeaders((arr) => [...arr, { role: '', name: '', photo: '' }]);
  const delLeader = (i) => setLeaders((arr) => arr.filter((_, j) => j !== i));

  if (!loaded) return <p style={{ color: theme.color.inkSoft }}>Loading content…</p>;

  return (
    <>
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Contact details</h3>
        <Field><label>Email</label><input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></Field>
        <Field><label>Phone</label><input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></Field>
        <Field><label>Address</label><input value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} /></Field>
        <Field><label>Postal</label><input value={contact.postal} onChange={(e) => setContact({ ...contact, postal: e.target.value })} /></Field>
        <Btn onClick={() => save('contact', contact)}>Save contact</Btn>{savedKey === 'contact' && <Saved>Saved ✓</Saved>}
      </Card>

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Purpose, mission & vision</h3>
        <Field><label>Purpose</label><textarea value={intro.purpose} onChange={(e) => setIntro({ ...intro, purpose: e.target.value })} /></Field>
        <Field><label>Mission</label><textarea value={intro.mission} onChange={(e) => setIntro({ ...intro, mission: e.target.value })} /></Field>
        <Field><label>Vision</label><textarea value={intro.vision} onChange={(e) => setIntro({ ...intro, vision: e.target.value })} /></Field>
        <Btn onClick={() => save('intro', intro)}>Save mission/vision</Btn>{savedKey === 'intro' && <Saved>Saved ✓</Saved>}
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Home hero slides</h3><Btn $ghost onClick={addSlide}>+ Add slide</Btn>
        </div>
        {slides.length === 0 && <p style={{ color: theme.color.inkSoft, fontSize: '0.9rem' }}>No custom slides yet — the site shows its built-in slides. Add one to override them.</p>}
        {slides.map((s, i) => (
          <div key={i} style={{ borderTop: `1px solid ${theme.color.line}`, paddingTop: '1.2rem', marginTop: '1.2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ color: theme.color.inkSoft }}>Slide {i + 1}</strong>
              <button onClick={() => delSlide(i)} style={{ background: 'none', border: 'none', color: '#b3261e', cursor: 'pointer', fontWeight: 700 }}>Remove</button>
            </div>
            <Field><label>Kicker</label><input value={s.kicker || ''} onChange={(e) => setSlide(i, { kicker: e.target.value })} /></Field>
            <Field><label>Title</label><input value={s.title || ''} onChange={(e) => setSlide(i, { title: e.target.value })} /></Field>
            <Field><label>Subtitle</label><textarea value={s.sub || ''} onChange={(e) => setSlide(i, { sub: e.target.value })} /></Field>
            <ImageField label="Image" value={s.image} onChange={(url) => setSlide(i, { image: url })} />
          </div>
        ))}
        <div style={{ marginTop: '1.4rem' }}><Btn onClick={() => save('heroSlides', slides)}>Save slides</Btn>{savedKey === 'heroSlides' && <Saved>Saved ✓</Saved>}</div>
      </Card>

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Leadership — Director</h3>
        <Field><label>Role</label><input value={director.role || ''} onChange={(e) => setDirector({ ...director, role: e.target.value })} /></Field>
        <Field><label>Name</label><input value={director.name || ''} onChange={(e) => setDirector({ ...director, name: e.target.value })} /></Field>
        <Field><label>Short bio</label><textarea value={director.bio || ''} onChange={(e) => setDirector({ ...director, bio: e.target.value })} /></Field>
        <ImageField label="Photo" value={director.photo} onChange={(url) => setDirector({ ...director, photo: url })} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.6rem 0 0.4rem' }}>
          <h3>Team members</h3><Btn $ghost onClick={addLeader}>+ Add member</Btn>
        </div>
        {leaders.length === 0 && <p style={{ color: theme.color.inkSoft, fontSize: '0.9rem' }}>No custom team yet — the About page shows its built-in roles. Add members to override them.</p>}
        {leaders.map((m, i) => (
          <div key={i} style={{ borderTop: `1px solid ${theme.color.line}`, paddingTop: '1.2rem', marginTop: '1.2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ color: theme.color.inkSoft }}>Member {i + 1}</strong>
              <button onClick={() => delLeader(i)} style={{ background: 'none', border: 'none', color: '#b3261e', cursor: 'pointer', fontWeight: 700 }}>Remove</button>
            </div>
            <Field><label>Role</label><input value={m.role || ''} onChange={(e) => setLeader(i, { role: e.target.value })} /></Field>
            <Field><label>Name</label><input value={m.name || ''} onChange={(e) => setLeader(i, { name: e.target.value })} /></Field>
            <ImageField label="Photo" value={m.photo} onChange={(url) => setLeader(i, { photo: url })} />
          </div>
        ))}
        <div style={{ marginTop: '1.4rem' }}>
          <Btn onClick={() => save('leadership', { director, leaders })}>Save leadership</Btn>{savedKey === 'leadership' && <Saved>Saved ✓</Saved>}
        </div>
      </Card>
    </>
  );
}

// ── News tab ────────────────────────────────────────────────
const blankPost = { title: '', date_label: '', excerpt: '', body: '', image: '', likes: 0 };

function NewsManager() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // post object or null
  const [busy, setBusy] = useState(false);

  const load = () => fetchNews().then((d) => setRows(d || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const row = { ...editing };
      if (!row.id) delete row.id;
      await saveNews(row);
      setEditing(null); await load();
    } catch (e) { alert(e.message || 'Save failed'); } finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await removeNews(id); await load(); } catch (e) { alert(e.message || 'Delete failed'); }
  };

  if (!rows) return <p style={{ color: theme.color.inkSoft }}>Loading news…</p>;

  if (editing) {
    return (
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>{editing.id ? 'Edit post' : 'New post'}</h3>
        <Field><label>Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
        <Field><label>Label (e.g. Community, Admissions)</label><input value={editing.date_label || ''} onChange={(e) => setEditing({ ...editing, date_label: e.target.value })} /></Field>
        <Field><label>Excerpt (short summary on the card)</label><textarea value={editing.excerpt || ''} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} /></Field>
        <Field><label>Full story (leave a blank line between paragraphs)</label><textarea style={{ minHeight: 180 }} value={editing.body || ''} onChange={(e) => setEditing({ ...editing, body: e.target.value })} /></Field>
        <ImageField label="Image" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.6rem' }}>
          <Btn onClick={onSave} disabled={busy || !editing.title}>{busy ? 'Saving…' : 'Save post'}</Btn>
          <Btn $ghost onClick={() => setEditing(null)}>Cancel</Btn>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.4rem' }}>
        <h3>News posts</h3><Btn onClick={() => setEditing({ ...blankPost })}>+ New post</Btn>
      </div>
      {rows.length === 0 ? (
        <p style={{ color: theme.color.inkSoft }}>No posts yet. The public News page shows built-in samples until you add posts here.</p>
      ) : (
        <Table>
          {rows.map((p) => (
            <div className="row" key={p.id} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <div><strong>{p.title}</strong><div className="muted">{p.date_label}</div></div>
              <div className="muted">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</div>
              <div className="acts">
                <Btn $ghost onClick={() => setEditing(p)}>Edit</Btn>
                <Btn $ghost onClick={() => onDelete(p.id)}>Delete</Btn>
              </div>
            </div>
          ))}
        </Table>
      )}
    </>
  );
}

// ── Programmes tab ──────────────────────────────────────────
const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const blankProgram = { slug: '', title: '', faculty: 'Agriculture', level: 'Certificate', duration: '', mode: 'Full-time · hands-on', tuition: 'Enquire', summary: '', image: '', sort: 0 };

function ProgramsManager() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => fetchPrograms().then((d) => setRows(d || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const row = { ...editing };
      delete row._isNew;
      if (!row.slug) row.slug = slugify(row.title);
      row.sort = Number(row.sort) || 0;
      await saveProgram(row);
      setEditing(null); await load();
    } catch (e) { alert(e.message || 'Save failed'); } finally { setBusy(false); }
  };
  const onDelete = async (s) => {
    if (!window.confirm('Delete this course?')) return;
    try { await removeProgram(s); await load(); } catch (e) { alert(e.message || 'Delete failed'); }
  };

  if (!rows) return <p style={{ color: theme.color.inkSoft }}>Loading programmes…</p>;

  if (editing) {
    return (
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>{editing._isNew ? 'New course' : 'Edit course'}</h3>
        <Field><label>Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field><label>Department</label>
            <select value={editing.faculty} onChange={(e) => setEditing({ ...editing, faculty: e.target.value })}
              style={{ width: '100%', padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
              {FACULTIES.map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field><label>Level</label>
            <select value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })}
              style={{ width: '100%', padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
              {LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field><label>Duration</label><input value={editing.duration || ''} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} /></Field>
          <Field><label>Tuition</label><input value={editing.tuition || ''} onChange={(e) => setEditing({ ...editing, tuition: e.target.value })} /></Field>
        </div>
        <Field><label>Mode</label><input value={editing.mode || ''} onChange={(e) => setEditing({ ...editing, mode: e.target.value })} /></Field>
        <Field><label>Summary</label><textarea value={editing.summary || ''} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></Field>
        <ImageField label="Image" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.6rem' }}>
          <Btn onClick={onSave} disabled={busy || !editing.title}>{busy ? 'Saving…' : 'Save course'}</Btn>
          <Btn $ghost onClick={() => setEditing(null)}>Cancel</Btn>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.4rem' }}>
        <h3>Courses</h3><Btn onClick={() => setEditing({ ...blankProgram, _isNew: true })}>+ New course</Btn>
      </div>
      {rows.length === 0 ? (
        <p style={{ color: theme.color.inkSoft }}>No courses in the database yet — the public site shows the built-in 13 courses until you add your own here.</p>
      ) : (
        <Table>
          {rows.map((p) => (
            <div className="row" key={p.slug} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <div><strong>{p.title}</strong><div className="muted">{p.faculty} · {p.level}</div></div>
              <div className="muted">{p.tuition}</div>
              <div className="acts">
                <Btn $ghost onClick={() => setEditing(p)}>Edit</Btn>
                <Btn $ghost onClick={() => onDelete(p.slug)}>Delete</Btn>
              </div>
            </div>
          ))}
        </Table>
      )}
    </>
  );
}

// ── Fees tab ────────────────────────────────────────────────
function FeesManager() {
  const [loaded, setLoaded] = useState(false);
  const [fees, setFees] = useState({ intro: '', pdf: '' });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSiteContent().then((c) => { if (c.fees) setFees((p) => ({ ...p, ...c.fees })); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const pickPdf = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const url = await uploadMedia(file); setFees((p) => ({ ...p, pdf: url })); } catch (er) { alert(er.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try { await saveSiteContent('fees', fees); setSaved(true); setTimeout(() => setSaved(false), 2500); } catch (e) { alert(e.message || 'Save failed'); }
  };

  if (!loaded) return <p style={{ color: theme.color.inkSoft }}>Loading…</p>;

  return (
    <Card>
      <h3 style={{ marginBottom: '1rem' }}>Fees</h3>
      <Field><label>Intro text (shown on the Fees page)</label><textarea value={fees.intro} onChange={(e) => setFees({ ...fees, intro: e.target.value })} /></Field>
      <Field>
        <label>Fee structure PDF</label>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {fees.pdf ? <a href={fees.pdf} target="_blank" rel="noreferrer" style={{ color: theme.color.accent, fontWeight: 700 }}>Current PDF ↗</a> : <span style={{ color: theme.color.inkSoft }}>None uploaded</span>}
          <label style={{ cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>
            {busy ? 'Uploading…' : 'Upload PDF'}
            <input type="file" accept="application/pdf" onChange={pickPdf} style={{ display: 'none' }} />
          </label>
        </div>
      </Field>
      <Btn onClick={save}>Save fees</Btn>{saved && <Saved>Saved ✓</Saved>}
    </Card>
  );
}

// ── Library tab ─────────────────────────────────────────────
const blankBook = { slug: '', title: '', type: 'Manual', department: 'General', file_path: '', featured: false, sort: 0 };

function LibraryManager() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => fetchLibrary().then((d) => setRows(d || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true); setMsg('');
    try {
      const row = { ...editing };
      delete row._isNew;
      if (!row.slug) row.slug = librarySlug(row.title);
      row.sort = Number(row.sort) || 0;
      await saveLibraryItem(row);
      setEditing(null); await load();
    } catch (e) { setMsg('Error: ' + (e.message || 'save failed')); } finally { setBusy(false); }
  };

  const onDelete = async (slug) => {
    if (!window.confirm('Delete this title? Its PDF will be removed too.')) return;
    try { await removeLibraryItem(slug); await load(); } catch (e) { alert(e.message || 'Delete failed'); }
  };

  const pickPdf = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setMsg('');
    try {
      const slug = editing.slug || librarySlug(editing.title);
      if (!slug) { setMsg('Error: give the title a name first.'); return; }
      const path = await uploadLibraryFile(file, slug);
      setEditing((p) => ({ ...p, slug, file_path: path }));
    } catch (er) { setMsg('Error: ' + (er.message || 'upload failed')); } finally { setBusy(false); }
  };

  const preview = async (filePath) => {
    const url = await signLibraryFile(filePath);
    if (url) window.open(url, '_blank', 'noopener');
    else alert('No file uploaded for this title yet.');
  };

  // One-click migration of the list that used to be hardcoded in the page.
  const seed = async () => {
    if (!window.confirm(`Add the ${BUILT_IN_LIBRARY.length} built-in titles to the database? Existing entries with the same name are left alone.`)) return;
    setBusy(true); setMsg('');
    try {
      const existing = new Set((rows || []).map((r) => r.slug));
      const toAdd = BUILT_IN_LIBRARY.filter((b) => !existing.has(b.slug));
      for (const b of toAdd) await saveLibraryItem(b);
      setMsg(`Added ${toAdd.length} title${toAdd.length === 1 ? '' : 's'}.`);
      await load();
    } catch (e) { setMsg('Error: ' + (e.message || 'could not seed')); } finally { setBusy(false); }
  };

  if (!rows) return <p style={{ color: theme.color.inkSoft }}>Loading library…</p>;

  if (editing) {
    return (
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>{editing._isNew ? 'New title' : 'Edit title'}</h3>
        <Field><label>Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field><label>Type</label>
            <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              style={{ width: '100%', padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
              {LIBRARY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field><label>Department</label>
            <select value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })}
              style={{ width: '100%', padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
              {LIBRARY_DEPTS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
        </div>

        <Field>
          <label>PDF</label>
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {editing.file_path
              ? <button type="button" onClick={() => preview(editing.file_path)}
                  style={{ background: 'none', border: 'none', color: theme.color.accent, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                  {editing.file_path} ↗
                </button>
              : <span style={{ color: theme.color.inkSoft, fontSize: '0.9rem' }}>No file uploaded</span>}
            <label style={{ cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>
              {busy ? 'Uploading…' : editing.file_path ? 'Replace PDF' : 'Upload PDF'}
              <input type="file" accept="application/pdf" onChange={pickPdf} style={{ display: 'none' }} />
            </label>
          </div>
          <p style={{ fontSize: '0.8rem', color: theme.color.inkSoft, marginTop: '0.35rem' }}>
            Stored privately — students only ever get a link that expires after 10 minutes.
          </p>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
          <Field><label>Sort order</label><input type="number" value={editing.sort} onChange={(e) => setEditing({ ...editing, sort: e.target.value })} /></Field>
          <Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none', letterSpacing: 0, fontSize: '0.9rem' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={Boolean(editing.featured)}
                onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
              Show under “Popular this term”
            </label>
          </Field>
        </div>

        {msg && <p style={{ fontSize: '0.88rem', color: msg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{msg}</p>}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.6rem' }}>
          <Btn onClick={onSave} disabled={busy || !editing.title}>{busy ? 'Saving…' : 'Save title'}</Btn>
          <Btn $ghost onClick={() => { setEditing(null); setMsg(''); }}>Cancel</Btn>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.4rem', gap: '0.6rem', flexWrap: 'wrap' }}>
        <h3>Library catalogue</h3>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          {rows.length === 0 && <Btn $ghost onClick={seed} disabled={busy}>Add built-in titles</Btn>}
          <Btn onClick={() => setEditing({ ...blankBook, _isNew: true, sort: rows.length })}>+ New title</Btn>
        </div>
      </div>

      {msg && <p style={{ fontSize: '0.88rem', marginBottom: '1rem', color: msg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{msg}</p>}

      {rows.length === 0 ? (
        <p style={{ color: theme.color.inkSoft }}>
          Nothing in the database yet — the Library page is showing its {BUILT_IN_LIBRARY.length} built-in titles.
          Use “Add built-in titles” to bring them in here so you can edit them, or add your own.
        </p>
      ) : (
        <Table>
          {rows.map((b) => (
            <div className="row" key={b.slug} style={{ gridTemplateColumns: '2fr 1fr 1fr auto' }}>
              <div>
                <strong>{b.title}</strong>
                <div className="muted">{b.file_path ? b.file_path : 'no PDF uploaded'}</div>
              </div>
              <div className="muted">{b.department}</div>
              <div className="muted">{b.type}{b.featured ? ' · featured' : ''}</div>
              <div className="acts">
                <Btn $ghost onClick={() => setEditing(b)}>Edit</Btn>
                <Btn $ghost onClick={() => onDelete(b.slug)}>Delete</Btn>
              </div>
            </div>
          ))}
        </Table>
      )}
    </>
  );
}

// ── Portal tab (notices, documents, results) ───────────────
function PortalManager() {
  const [notices, setNotices] = useState([]);
  const [docs, setDocs] = useState([]);
  const [results, setResults] = useState([]);
  const [n, setN] = useState({ title: '', body: '', audience: 'all' });
  const [d, setD] = useState({ title: '', url: '', audience: 'all' });
  const [r, setR] = useState({ who: '', title: '', detail: '' }); // who = Student ID or email
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetchNotices().then(setNotices);
    fetchDocuments().then(setDocs);
    fetchResults().then(setResults);
  };
  useEffect(() => { load(); }, []);

  const addNotice = async () => { setBusy(true); try { await saveNotice(n); setN({ title: '', body: '', audience: 'all' }); load(); } catch (e) { alert(e.message); } finally { setBusy(false); } };
  const addDoc = async () => { setBusy(true); try { await saveDocument(d); setD({ title: '', url: '', audience: 'all' }); load(); } catch (e) { alert(e.message); } finally { setBusy(false); } };
  // Results are stored against the login email, but admins usually know the
  // Student ID — resolve whichever was typed to the account's real address.
  const addResult = async () => {
    setBusy(true);
    try {
      const student_email = await resolveAccountEmail(r.who);
      await saveResult({ student_email, title: r.title, detail: r.detail });
      setR({ who: '', title: '', detail: '' }); load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const pickDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const url = await uploadMedia(file); setD((p) => ({ ...p, url })); } catch (er) { alert(er.message); } finally { setBusy(false); }
  };

  const Audience = ({ value, onChange }) => (
    <select value={value} onChange={onChange} style={{ padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
      <option value="all">Everyone</option><option value="student">Students</option><option value="staff">Staff</option>
    </select>
  );

  return (
    <>
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Notices</h3>
        <Field><label>Title</label><input value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} /></Field>
        <Field><label>Body</label><textarea value={n.body} onChange={(e) => setN({ ...n, body: e.target.value })} /></Field>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <Audience value={n.audience} onChange={(e) => setN({ ...n, audience: e.target.value })} />
          <Btn onClick={addNotice} disabled={busy || !n.title}>Post notice</Btn>
        </div>
        <Table style={{ marginTop: '1.4rem' }}>
          {notices.map((x) => (
            <div className="row" key={x.id} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <div><strong>{x.title}</strong><div className="muted">{x.body}</div></div>
              <div className="muted">{x.audience}</div>
              <div className="acts"><Btn $ghost onClick={async () => { await removeNotice(x.id); load(); }}>Delete</Btn></div>
            </div>
          ))}
        </Table>
      </Card>

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Documents</h3>
        <Field><label>Title</label><input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} /></Field>
        <Field>
          <label>File / link</label>
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <input value={d.url} onChange={(e) => setD({ ...d, url: e.target.value })} placeholder="URL or upload →" />
            <label style={{ whiteSpace: 'nowrap', cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>
              {busy ? 'Uploading…' : 'Upload'}<input type="file" onChange={pickDoc} style={{ display: 'none' }} />
            </label>
          </div>
        </Field>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <Audience value={d.audience} onChange={(e) => setD({ ...d, audience: e.target.value })} />
          <Btn onClick={addDoc} disabled={busy || !d.title}>Add document</Btn>
        </div>
        <Table style={{ marginTop: '1.4rem' }}>
          {docs.map((x) => (
            <div className="row" key={x.id} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <div><strong>{x.title}</strong></div>
              <div className="muted">{x.audience}</div>
              <div className="acts"><Btn $ghost onClick={async () => { await removeDocument(x.id); load(); }}>Delete</Btn></div>
            </div>
          ))}
        </Table>
      </Card>

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Student results</h3>
        <Field>
          <label>Student ID or email</label>
          <input value={r.who} onChange={(e) => setR({ ...r, who: e.target.value })} placeholder="MTI/2026/001 or student@email.com" />
          <p style={{ fontSize: '0.8rem', color: theme.color.inkSoft, marginTop: '0.35rem' }}>
            Either works — the ID is matched to the account automatically.
          </p>
        </Field>
        <Field><label>Title (e.g. Term 1 report)</label><input value={r.title} onChange={(e) => setR({ ...r, title: e.target.value })} /></Field>
        <Field><label>Detail / grades</label><textarea value={r.detail} onChange={(e) => setR({ ...r, detail: e.target.value })} /></Field>
        <Btn onClick={addResult} disabled={busy || !r.who || !r.title}>Publish result</Btn>
        <Table style={{ marginTop: '1.4rem' }}>
          {results.map((x) => (
            <div className="row" key={x.id} style={{ gridTemplateColumns: '1.4fr 1.6fr auto' }}>
              <div className="muted">{x.student_email}</div>
              <div><strong>{x.title}</strong></div>
              <div className="acts"><Btn $ghost onClick={async () => { await removeResult(x.id); load(); }}>Delete</Btn></div>
            </div>
          ))}
        </Table>
      </Card>
    </>
  );
}

// ── Students tab (provision accounts) ──────────────────────
function StudentsManager() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ studentId: '', fullName: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => fetchStudents().then(setList);
  useEffect(() => { load(); }, []);

  const create = async () => {
    setMsg(''); setBusy(true);
    try {
      await createStudentAccount(form);
      setMsg(`Created ${form.studentId}. They log in with that ID and the temporary password, then set their own.`);
      setForm({ studentId: '', fullName: '', password: '' });
      load();
    } catch (e) { setMsg('Error: ' + (e.message || 'could not create account')); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Create a student account</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field><label>Student ID</label><input value={form.studentId} onChange={(e) => setForm({ ...form, studentId: e.target.value })} placeholder="MTI/2026/001" /></Field>
          <Field><label>Full name</label><input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></Field>
        </div>
        <Field><label>Temporary password</label><input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="give this to the student" /></Field>
        <Btn onClick={create} disabled={busy || !form.studentId || !form.password}>{busy ? 'Creating…' : 'Create account'}</Btn>
        {msg && <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: msg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{msg}</p>}
        <p style={{ marginTop: '0.8rem', fontSize: '0.82rem', color: theme.color.inkSoft }}>
          Requires the <code>create-student</code> Edge Function to be deployed (see STUDENT_ACCOUNTS_SETUP.md).
        </p>
      </Card>

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Students ({list.length})</h3>
        {list.length === 0 ? <p style={{ color: theme.color.inkSoft }}>No students yet.</p> : (
          <Table>
            {list.map((s) => (
              <div className="row" key={s.id} style={{ gridTemplateColumns: '1.4fr 1.6fr 1fr auto' }}>
                <div><strong>{s.student_id || '—'}</strong></div>
                <div>{s.full_name || s.email}</div>
                <div className="muted">{s.must_change_password ? 'pending first login' : 'active'}</div>
                <div className="acts">
                  <Btn $ghost onClick={async () => {
                    if (!window.confirm(`Promote ${s.full_name || s.student_id} to teacher? They'll move to the Teachers tab.`)) return;
                    await setProfileRole(s.id, 'staff'); load();
                  }}>Make teacher</Btn>
                </div>
              </div>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

// ── Admin allowlist ─────────────────────────────────────────
function AdminsManager() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('content'); // least privilege by default
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => fetchAdmins().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setMsg(''); setBusy(true);
    try {
      const added = await addAdmin(email, tier);
      setMsg(`${added} added as ${tier === 'owner' ? 'an owner' : 'a content manager'}.`);
      setEmail(''); load();
    } catch (e) { setMsg('Error: ' + (e.message || 'could not add that admin')); }
    finally { setBusy(false); }
  };

  const changeTier = async (a, role) => {
    const warn = role === 'owner'
      ? `Make ${a.email} an owner? They'll be able to create accounts, approve applicants and manage this list.`
      : `Reduce ${a.email} to content manager? They'll lose access to accounts and this list.`;
    if (!window.confirm(warn)) return;
    setMsg('');
    try { await setAdminRole(a.email, role); load(); }
    catch (e) { setMsg('Error: ' + (e.message || 'could not change that')); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    setMsg('');
    try { await removeAdmin(a.email); load(); }
    catch (e) { setMsg('Error: ' + (e.message || 'could not remove that admin')); }
  };

  const me = (user?.email || '').toLowerCase();

  return (
    <Card>
      <h3 style={{ marginBottom: '0.5rem' }}>Admins ({list.length})</h3>
      <p style={{ fontSize: '0.85rem', color: theme.color.inkSoft, marginBottom: '1rem' }}>
        Admins can see everything here, including applicant personal details. Keep this list short and
        check it occasionally — an account you don’t recognise is worth asking about.
      </p>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com"
          onKeyDown={(e) => e.key === 'Enter' && email && add()}
          style={{ flex: '1 1 220px', padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
        <select value={tier} onChange={(e) => setTier(e.target.value)}
          style={{ padding: '10px 13px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }}>
          <option value="content">Content manager</option>
          <option value="owner">Owner</option>
        </select>
        <Btn onClick={add} disabled={busy || !email}>{busy ? 'Adding…' : 'Add'}</Btn>
      </div>
      <p style={{ fontSize: '0.8rem', color: theme.color.inkSoft, marginTop: '0.5rem', lineHeight: 1.5 }}>
        <strong>Content manager</strong> — website content, programmes, fees, news, library, notices,
        documents and results. Can read applications but not approve or onboard.<br />
        <strong>Owner</strong> — all of that, plus creating accounts, approving applicants and managing
        this list.<br />
        The address doesn’t need an account yet — access starts the moment they sign in with it.
      </p>

      {msg && <p style={{ marginTop: '0.9rem', fontSize: '0.88rem', color: msg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{msg}</p>}

      <Table style={{ marginTop: '1.4rem' }}>
        {list.map((a) => {
          const isMe = a.email === me;
          const owner = (a.role || 'owner') === 'owner';
          const lastOwner = owner && list.filter((x) => (x.role || 'owner') === 'owner').length <= 1;
          return (
            <div className="row" key={a.email} style={{ gridTemplateColumns: '1.8fr 0.8fr 1.2fr auto' }}>
              <div>
                <strong>{a.email}</strong>{isMe && <span style={{ color: theme.color.accent, fontSize: '0.78rem', fontWeight: 700 }}> · you</span>}
              </div>
              <div>
                <Badge $s={owner ? 'enrolled' : 'approved'}>{owner ? 'Owner' : 'Content'}</Badge>
              </div>
              <div className="muted">
                {a.added_by ? `by ${a.added_by}` : 'added in Supabase'}
                {a.added_at ? ` · ${new Date(a.added_at).toLocaleDateString()}` : ''}
              </div>
              <div className="acts">
                {/* All of these are blocked in the database too - disabling
                    here only saves a pointless round trip. */}
                <Btn $ghost onClick={() => changeTier(a, owner ? 'content' : 'owner')} disabled={isMe || lastOwner}>
                  {owner ? 'Make content' : 'Make owner'}
                </Btn>
                <Btn $ghost onClick={() => remove(a)} disabled={isMe || lastOwner}>Remove</Btn>
              </div>
            </div>
          );
        })}
      </Table>
      {list.filter((x) => (x.role || 'owner') === 'owner').length <= 1 && (
        <p style={{ fontSize: '0.8rem', color: theme.color.inkSoft, marginTop: '0.8rem' }}>
          There’s only one owner. Add a second before removing or demoting them — the last owner is
          protected, to avoid locking everybody out of account management.
        </p>
      )}
    </Card>
  );
}

// ── Teachers tab (provision & manage staff accounts) ───────
function TeachersManager() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: '', fullName: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => fetchStaff().then(setList);
  useEffect(() => { load(); }, []);

  const create = async () => {
    setMsg(''); setBusy(true);
    try {
      await createStaffAccount(form);
      setMsg(`Created ${form.email}. They sign in with that email and the temporary password, then set their own.`);
      setForm({ email: '', fullName: '', password: '' });
      load();
    } catch (e) { setMsg('Error: ' + (e.message || 'could not create account')); }
    finally { setBusy(false); }
  };

  const demote = async (t) => {
    if (!window.confirm(`Remove teacher access for ${t.full_name || t.email}? They become a student account.`)) return;
    try { await setProfileRole(t.id, 'student'); load(); } catch (e) { alert(e.message); }
  };

  return (
    <>
      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Add a teacher</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field><label>Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teacher@mti.ac.zm" /></Field>
          <Field><label>Full name</label><input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></Field>
        </div>
        <Field><label>Temporary password</label><input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="give this to the teacher" /></Field>
        <Btn onClick={create} disabled={busy || !form.email || !form.password}>{busy ? 'Creating…' : 'Create teacher account'}</Btn>
        {msg && <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: msg.startsWith('Error') ? '#b3261e' : '#1a7f4b' }}>{msg}</p>}
        <p style={{ marginTop: '0.8rem', fontSize: '0.82rem', color: theme.color.inkSoft }}>
          Teachers sign in with a real email (not a Student ID) and land on the staff portal, where they
          create classes, enrol students, post announcements and set assignments.
          Full <strong>admin</strong> rights are separate — use the Admins list above.
        </p>
      </Card>

      <AdminsManager />

      <Card>
        <h3 style={{ marginBottom: '1rem' }}>Teachers & staff ({list.length})</h3>
        {list.length === 0 ? <p style={{ color: theme.color.inkSoft }}>No teachers yet.</p> : (
          <Table>
            {list.map((t) => (
              <div className="row" key={t.id} style={{ gridTemplateColumns: '1.6fr 1.4fr 1fr auto' }}>
                <div><strong>{t.full_name || '—'}</strong><div className="muted">{t.email}</div></div>
                <div className="muted">{t.role}</div>
                <div className="muted">{t.must_change_password ? 'pending first login' : 'active'}</div>
                <div className="acts"><Btn $ghost onClick={() => demote(t)}>Remove access</Btn></div>
              </div>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

// ── Page shell ──────────────────────────────────────────────
function Admin() {
  const { user, isAdmin, isOwner, loading, ready, signOut } = useAuth();
  const [tab, setTab] = useState('applications');

  // Account management is owner-only. Hiding the tabs is a courtesy; the
  // policies in the database are what actually enforce it.
  const TABS = [
    ['applications', 'Applications'],
    ['content', 'Content'],
    ['programmes', 'Programmes'],
    ['fees', 'Fees'],
    ['news', 'News'],
    ['library', 'Library'],
    ['portal', 'Portal'],
    ...(isOwner ? [['students', 'Students'], ['teachers', 'Teachers']] : []),
  ];

  // If a tab disappears (tier changed mid-session), fall back to the first.
  useEffect(() => {
    if (!TABS.some(([k]) => k === tab)) setTab('applications');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  let body = null;
  if (!ready) {
    body = <Note><h2>Backend not connected</h2><p>Add your Supabase keys to <code>.env</code> to enable the admin area.</p><Link to="/">Back home</Link></Note>;
  } else if (loading) {
    body = <Section><p style={{ color: theme.color.inkSoft }}>Checking your access…</p></Section>;
  } else if (!user) {
    body = <Note><h2>Please sign in</h2><p>The admin area is for institute staff.</p><Link to="/login">Go to login</Link></Note>;
  } else if (!isAdmin) {
    body = <Note><h2>Not authorised</h2><p>This account isn’t an admin. Ask an administrator to add your email to the admins list.</p><Link to="/">Back home</Link></Note>;
  }

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">Staff area</span>
          <h1>Admin dashboard</h1>
          {ready && user && (
            <p className="who">
              Signed in as {user.email}
              {isAdmin && ` · ${isOwner ? 'owner' : 'content manager'}`}
              {' · '}<button onClick={signOut}>Sign out</button>
            </p>
          )}
        </div>
      </Header>

      {body || (
        <>
          <Tabs>
            {TABS.map(([key, label]) => (
              <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>
            ))}
          </Tabs>
          <Section>
            {tab === 'applications' && <Applications />}
            {tab === 'content' && <Content />}
            {tab === 'programmes' && <ProgramsManager />}
            {tab === 'fees' && <FeesManager />}
            {tab === 'news' && <NewsManager />}
            {tab === 'library' && <LibraryManager />}
            {tab === 'portal' && <PortalManager />}
            {tab === 'students' && isOwner && <StudentsManager />}
            {tab === 'teachers' && isOwner && <TeachersManager />}
          </Section>
        </>
      )}

      <Footer />
    </>
  );
}

export default Admin;
